<template>
    <b-modal :active="showModal" :can-cancel="false" :width="680" scroll="keep">
        <div class="card">
            <div class="card-content">
                <h2 class="title is-4"><i class="fad fa-user-check mr-3 has-text-primary"></i>Let's make sure we're up to date</h2>
                <p class="mb-5 has-text-grey">
                    It's been a while since we've checked in. Please take a moment to verify your information so
                    our records and your certification reporting stay accurate. You can also update this anytime
                    under <strong>Your Account</strong>.
                </p>

                <section v-if="step === 1">
                    <p class="has-text-weight-bold mb-4"><span class="tag is-primary mr-2">Step 1 of 2</span>Contact Information</p>

                    <div class="columns">
                        <div class="column is-3">
                            <b-field label="Title" label-position="inside">
                                <b-select v-model="profile.title" expanded>
                                    <option v-for="option in titleOptions" :value="option">{{ option }}</option>
                                </b-select>
                            </b-field>
                        </div>
                        <div class="column">
                            <gender-field v-model="profile.gender"/>
                        </div>
                    </div>
                    <div class="columns">
                        <div class="column">
                            <b-field label="Primary E-Mail" label-position="inside">
                                <b-input v-model="profile.email"></b-input>
                            </b-field>
                        </div>
                        <div class="column">
                            <b-field label="Secondary E-Mail" label-position="inside">
                                <b-input v-model="profile.email_2"></b-input>
                            </b-field>
                        </div>
                    </div>
                    <div class="columns">
                        <div class="column">
                            <b-field label="Home (Cell) Phone" label-position="inside">
                                <b-input v-model="profile.phone" v-mask="'(###) ###-####'"></b-input>
                            </b-field>
                        </div>
                        <div class="column">
                            <b-field label="Work Phone" label-position="inside">
                                <b-input v-model="profile.phone_2" v-mask="'(###) ###-####'"></b-input>
                            </b-field>
                        </div>
                    </div>
                    <div class="columns">
                        <div class="column is-7">
                            <b-field label="Address" label-position="inside">
                                <b-input v-model="profile.address"></b-input>
                            </b-field>
                        </div>
                        <div class="column">
                            <b-field label="Address 2" label-position="inside">
                                <b-input v-model="profile.address_2"></b-input>
                            </b-field>
                        </div>
                    </div>
                    <div class="columns">
                        <div class="column">
                            <b-field label="City" label-position="inside">
                                <b-input v-model="profile.city"></b-input>
                            </b-field>
                        </div>
                        <div class="column">
                            <b-field label="State" label-position="inside">
                                <b-select v-model="profile.state" expanded>
                                    <option v-for="(name, code) in states" :value="code">{{ name }}</option>
                                </b-select>
                            </b-field>
                        </div>
                        <div class="column">
                            <b-field label="Zip" label-position="inside">
                                <b-input v-model="profile.zip" v-mask="'#####'"></b-input>
                            </b-field>
                        </div>
                    </div>
                </section>

                <section v-if="step === 2">
                    <p class="has-text-weight-bold mb-4"><span class="tag is-primary mr-2">Step 2 of 2</span>A Few Quick Questions</p>

                    <!-- Per-certification switches (Account > Certifications & Organizations
                         is the full switchboard); pre-checked from the current switches -->
                    <div class="mb-5" v-if="certifications.length">
                        <p class="has-text-weight-bold mb-2">Which certifications are you tracking with us?</p>
                        <b-field v-for="cert in certifications" :key="cert.certification_id" class="mb-1">
                            <b-checkbox v-model="selectedCertificationIds" :native-value="cert.certification_id" type="is-info">{{ cert.certification.title }}</b-checkbox>
                        </b-field>
                        <p class="help">Turn on everything that applies. You can change this any time under <strong>Your Account &rsaquo; Certifications &amp; Organizations</strong>.</p>
                    </div>

                    <div class="columns">
                        <div class="column">
                            <b-field label="District" label-position="inside">
                                <b-select v-model="district_id" expanded>
                                    <option :value="0">Not Listed/Not Applicable</option>
                                    <option :value="999999">Private School</option>
                                    <option v-for="district in districts" v-if="district.id !== 9999999" :value="district.id">{{ district.name }} ({{ district.city | capitalize }})</option>
                                </b-select>
                            </b-field>
                        </div>
                        <div class="column">
                            <b-field label="School" label-position="inside">
                                <b-select v-model="school_id" expanded>
                                    <option :value="0">Not Listed/Not Applicable</option>
                                    <option v-for="school in schools" :value="school.id">{{ school.name }} ({{ school.city | capitalize }})</option>
                                </b-select>
                            </b-field>
                        </div>
                    </div>

                    <template v-if="clockHoursChecked">
                    <b-field label="Number of years teaching" class="mb-5">
                        <b-select v-model="meta.years_teaching" placeholder="Select..." expanded>
                            <option v-for="option in yearsOptions" :value="option">{{ option }}</option>
                        </b-select>
                    </b-field>
                    <b-field label="What is your highest degree level?" class="mb-5">
                        <b-select v-model="meta.highest_degree" placeholder="Select..." expanded>
                            <option v-for="option in degreeOptions" :value="option">{{ option }}</option>
                        </b-select>
                    </b-field>
                    <b-field label="For certificated educators, what is your current educational clock hour/credit column?" class="mb-5">
                        <b-select v-model="meta.hours_column" placeholder="Select..." expanded>
                            <option v-for="option in hoursOptions" :value="option">{{ option }}</option>
                        </b-select>
                    </b-field>

                    <p class="has-text-weight-bold mb-2 mt-5">Certificate renewal <span class="tag is-light ml-1">optional</span></p>
                    <pd-renewal-fields v-model="meta" intro="Tell us when your certificate renews and any clock hours you already have on file, and your dashboard will show your progress toward renewal." stacked/>
                    </template>
                </section>
            </div>
            <footer class="card-footer">
                <div class="card-footer-item is-justify-content-space-between">
                    <a class="has-text-grey" @click.prevent="snooze"><i class="far fa-clock mr-2"></i>Remind me later</a>
                    <div>
                        <b-button v-if="step === 2" class="mr-2" @click="step = 1">Back</b-button>
                        <b-button v-if="step === 1" type="is-primary" :disabled="!step1Valid" @click="step = 2">Next<i class="fas fa-arrow-right ml-2"></i></b-button>
                        <b-button v-else type="is-primary" :disabled="!step2Valid || processing" @click="save"><i class="fas fa-check mr-2"></i>Confirm &amp; Save</b-button>
                    </div>
                </div>
            </footer>
        </div>
    </b-modal>
</template>

<script>
    import {mapGetters, mapMutations} from "vuex";
    import {client as http} from "@/http_client";
    import {isPublicPath} from "@/router";
    import GenderField from "@/components/fields/GenderField";
    import PdRenewalFields from "@/components/fields/PdRenewalFields";
    import {TITLE_OPTIONS, YEARS_TEACHING_OPTIONS, DEGREE_OPTIONS, HOURS_COLUMN_OPTIONS} from "@/constants/profile";

    export default {
        name: "ProfileVerificationModal",
        components: {GenderField, PdRenewalFields},
        data: function() {
            return {
                due: false,
                dismissed: false,
                step: 1,
                processing: false,
                profile: {},
                meta: {years_teaching: null, highest_degree: null, hours_column: null, pd_renewal_on: null, pd_external_hours: null, pd_target_hours: null},
                districts: [],
                schools: [],
                district_id: undefined,
                school_id: undefined,
                initialSchoolId: null,
                // certification switches as shown in this modal (ids); seeded
                // from the store each time the modal loads
                selectedCertificationIds: [],
                titleOptions: TITLE_OPTIONS,
                yearsOptions: YEARS_TEACHING_OPTIONS,
                degreeOptions: DEGREE_OPTIONS,
                hoursOptions: HOURS_COLUMN_OPTIONS,
                states: {
                    AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California', CO: 'Colorado',
                    CT: 'Connecticut', DE: 'Delaware', DC: 'District Of Columbia', FL: 'Florida', GA: 'Georgia',
                    HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois', IN: 'Indiana', IA: 'Iowa', KS: 'Kansas',
                    KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine', MD: 'Maryland', MA: 'Massachusetts',
                    MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri', MT: 'Montana',
                    NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey', NM: 'New Mexico',
                    NY: 'New York', NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio', OK: 'Oklahoma',
                    OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina', SD: 'South Dakota',
                    TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont', VA: 'Virginia', WA: 'Washington',
                    WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming'
                }
            };
        },
        computed: {
            ...mapGetters(['currentUser', 'profilePrompt', 'certifications']),
            ...mapGetters({storeSelectedIds: 'selectedCertificationIds'}),
            clockHoursChecked: function() {
                // no rows loaded yet → behave as before (ask everything)
                return !this.certifications.length || this.selectedCertificationIds.includes(1);
            },
            showModal: function() {
                return ((this.due && !this.dismissed) || this.profilePrompt.requested) && !!this.currentUser && !isPublicPath(this.$route.path);
            },
            step1Valid: function() {
                return !!(this.profile.gender && this.profile.email && this.profile.phone && this.profile.address && this.profile.city
                    && this.profile.state && this.profile.zip);
            },
            step2Valid: function() {
                if (this.district_id === undefined || this.school_id === undefined) return false;
                // the career questions only exist for users tracking WA clock hours
                if (!this.clockHoursChecked) return true;
                return !!(this.meta.years_teaching && this.meta.highest_degree && this.meta.hours_column);
            }
        },
        methods: {
            ...mapMutations(['setUser', 'setProfilePromptStatus', 'setProfilePromptRequested']),
            check: function() {
                if (!this.currentUser || !this.currentUser.id) return;
                http.get('app/users/profile-verification', {force: true}).then(response => {
                    this.setProfilePromptStatus({pending: response.data.pending, due: response.data.due});
                    if (!response.data.pending) return;
                    this.populate(response.data);
                    this.due = response.data.due;
                }).catch(err => {
                    console.error('Profile verification check failed.', err);
                });
            },
            populate: function(data) {
                this.profile = {...data.profile};
                this.meta = {...this.meta, ...data.meta};
                this.initialSchoolId = this.profile.school_id;
                this.loadDistricts();
                this.loadCertifications();
            },
            loadCertifications: function() {
                const seed = () => { this.selectedCertificationIds = [...this.storeSelectedIds]; };
                if (this.certifications.length) return seed();
                this.$store.dispatch('getCertifications').then(seed).catch(err => console.error(err));
            },
            // Persist any switch the user changed in this modal
            saveCertificationChanges: function() {
                const before = new Set(this.storeSelectedIds);
                const after = new Set(this.selectedCertificationIds);
                const changed = this.certifications.filter(c => before.has(c.certification_id) !== after.has(c.certification_id));
                return Promise.all(changed.map(c => this.$store.dispatch('saveCertification', {
                    certification_id: c.certification_id,
                    integration_id: c.integration_id,
                    certification_number: c.certification_number,
                    selected: after.has(c.certification_id),
                })));
            },
            // Summoned on demand (dashboard "Review My Info" / the
            // professional-development "Set up" link): load the current
            // answers whether or not a verification is pending
            loadForRequest: function() {
                if (!this.currentUser || !this.currentUser.id) return;
                http.get('app/users/profile-verification', {force: true}).then(response => {
                    this.populate(response.data);
                }).catch(err => {
                    console.error('Profile load failed.', err);
                });
            },
            loadDistricts: function() {
                http.get('districts').then(results => {
                    this.districts = results.data;
                    if (this.profile.district === 'Private School') {
                        this.district_id = 999999;
                    } else if (this.profile.district_id && this.districts.some(d => d.id === this.profile.district_id)) {
                        this.district_id = this.profile.district_id;
                    } else {
                        this.district_id = 0;
                    }
                }).catch(err => {
                    console.error(err);
                });
            },
            snooze: function() {
                // When the modal was summoned from the dashboard reminder,
                // closing it isn't a fresh dismissal — the snooze (and its
                // escalation counter) was already recorded when they first
                // said "remind me later".
                if (this.profilePrompt.requested) {
                    this.setProfilePromptRequested(false);
                    this.dismissed = true;
                    return;
                }
                this.dismissed = true;
                http.post('app/users/profile-verification/snooze').catch(err => {
                    console.error(err);
                });
            },
            save: function() {
                this.processing = true;
                http.post('app/users/profile-verification', {
                    title: this.profile.title,
                    gender: this.profile.gender,
                    email: this.profile.email,
                    email_2: this.profile.email_2,
                    phone: this.profile.phone,
                    phone_2: this.profile.phone_2,
                    address: this.profile.address,
                    address_2: this.profile.address_2,
                    city: this.profile.city,
                    state: this.profile.state,
                    zip: this.profile.zip,
                    district: this.profile.district,
                    district_id: (this.district_id === 0 || this.district_id === 999999) ? null : this.district_id,
                    school: this.profile.school,
                    school_id: (this.school_id === 0) ? null : this.school_id,
                    meta: this.meta
                }).then(() => this.saveCertificationChanges().catch(err => console.error('Certification switch save failed.', err))).then(() => {
                    this.due = false;
                    this.processing = false;
                    this.setProfilePromptStatus({pending: false, due: false});
                    this.setProfilePromptRequested(false);
                    // Keep the localStorage user snapshot in step with the edits.
                    const updated = {
                        ...this.currentUser,
                        title: this.profile.title,
                        gender: this.profile.gender,
                        email: this.profile.email,
                        email_2: this.profile.email_2,
                        email_alt: this.profile.email_2,
                        phone: this.profile.phone,
                        phone_2: this.profile.phone_2,
                        address: this.profile.address,
                        address_2: this.profile.address_2,
                        city: this.profile.city,
                        state: this.profile.state,
                        zip: this.profile.zip,
                        district: this.profile.district,
                        school: this.profile.school
                    };
                    localStorage.setItem('user', JSON.stringify(updated));
                    this.setUser(updated);
                    // The dashboard's professional-development section reads
                    // the renewal answers from the feed — refresh it
                    this.$store.dispatch('getDashboard').catch(() => {});
                    this.$notify('success', 'Thank You!', 'Your information has been verified and saved.', {duration: 5000, permanent: false});
                }).catch(err => {
                    this.processing = false;
                    const message = (err.response && err.response.data) ? err.response.data : 'Something went wrong saving your information. Please try again.';
                    this.$notify('error', 'Could Not Save', message, {duration: 8000, permanent: false});
                });
            }
        },
        watch: {
            currentUser: function(val, old) {
                if (val && val.id && (!old || old.id !== val.id)) this.check();
            },
            'profilePrompt.requested': function(val) {
                if (val) {
                    this.step = 1;
                    this.loadForRequest();
                }
            },
            district_id: function(val) {
                this.schools = [];
                if (val === 0) {
                    this.profile.district = 'Not Listed/Not Applicable';
                } else if (val === 999999) {
                    this.profile.district = 'Private School';
                } else {
                    let code;
                    this.districts.forEach(d => {
                        if (d.id === val) {
                            this.profile.district = d.name;
                            code = d.code;
                        }
                    });
                    if (code) {
                        http.get('districts/schoolsIn/' + code).then(results => {
                            this.schools = results.data;
                            if (this.initialSchoolId && this.schools.some(s => s.id === this.initialSchoolId)) {
                                this.school_id = this.initialSchoolId;
                            }
                            this.initialSchoolId = null;
                        }).catch(err => {
                            console.error(err);
                        });
                    }
                }
                if (val === 0 || val === 999999) {
                    this.school_id = 0;
                    this.initialSchoolId = null;
                } else if (!this.initialSchoolId) {
                    this.school_id = undefined;
                }
            },
            school_id: function(val) {
                if (val === 0) {
                    this.profile.school = 'Not Listed/Not Applicable';
                } else {
                    this.schools.forEach(s => {
                        if (s.id === val) this.profile.school = s.name;
                    });
                }
            }
        },
        mounted: function() {
            this.check();
        }
    };
</script>

<style scoped>
    .card-footer-item {
        display: flex;
        width: 100%;
        padding: 1rem 1.5rem;
    }
</style>
